import {
  NCRModal,
  NCRTable,
  type NCRTableProps,
} from "@dmca/frontend/widgets/admin/ncr";
import { Button } from "@dmca/frontend/components/button";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import { PageHeader } from "@dmca/frontend/components/nav";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import type { INCR } from "@dmca/models/interface/ncr";
import { Paper, Stack } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@dmca/frontend/trpc";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { DateTime } from "@dmca/utils/luxon";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/ncr")({
  component: RouteComponent,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("Admin::ManageNCR", context);
    const { trpcQcProxy: trpc, qc } = context;
    await qc.prefetchQuery(trpc.ncr.getAll.queryOptions());
  },
  head: () => ({
    meta: [
      {
        title: "Non-Conformance Reports | DNA Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState<INCR>();
  const [deleteRow, setDeleteRow] = useState<INCR | undefined>();

  const trpc = useTRPC();
  const qc = useQueryClient();
  const deleteMutation = useMutation(trpc.ncr.delete.mutationOptions());
  const exportMutation = useMutation(trpc.ncr.exportToXLSX.mutationOptions());

  const handleExport = useCallback(() => {
    exportMutation.mutate(undefined, {
      onError: (e) => {
        toast.error(
          `Error exporting NCR report: ${e.message || "Unknown error"}`
        );
      },
      onSuccess: async ({ data }) => {
        const paddedBase64 = data
          .padEnd(data.length + ((4 - (data.length % 4 || 4)) % 4), "=")
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        const response = await fetch(
          `data:application/octet-stream;base64,${paddedBase64}`
        );
        const blob = new Blob([await response.arrayBuffer()], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `NCR Report (${DateTime.now().toFormat(
          "MMM d yyyy"
        )}).xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
    });
  }, [exportMutation]);

  const handleContextMenuSelect: NCRTableProps["onContextMenuSelect"] = (
    row,
    type
  ) => {
    if (type === "edit") {
      setEditRow(row);
      setShowForm(true);
      return;
    }
    setDeleteRow(row);
  };

  return (
    <>
      <PageHeader>Non-Conformance Reports</PageHeader>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
      >
        <Paper sx={{ p: 2, width: "100%" }}>
          <Stack direction="row" gap={2}>
            <Button
              data-testid="ncr-main-page-export"
              loading={exportMutation.isPending}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              data-testid="ncr-main-page-open-report-btn"
              onClick={() => {
                setEditRow(undefined);
                setShowForm(true);
              }}
            >
              Open A Report
            </Button>
          </Stack>
        </Paper>
      </Stack>
      <NCRTable onContextMenuSelect={handleContextMenuSelect} />
      <NCRModal
        onClose={() => setShowForm(false)}
        open={showForm}
        editRow={editRow}
      />
      <ConfirmationModal
        header="Delete NCR"
        message="Are you sure you want to delete this report? This action cannot be undone."
        open={!!deleteRow}
        onClose={() => setDeleteRow(undefined)}
        onReject={() => setDeleteRow(undefined)}
        onConfirm={() => {
          deleteMutation.mutate(
            { id: deleteRow!.id },
            {
              onSuccess: () => {
                qc.invalidateQueries(trpc.ncr.getAll.queryFilter());
                setDeleteRow(undefined);
              },
              onError: (e) => {
                toast.error(
                  `Error deleting NCR: ${e.message || "Unknown error"}`
                );
                setDeleteRow(undefined);
              },
            }
          );
        }}
      />
    </>
  );
}
